import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '../ui/button';
import {
  ArrowLeft, ArrowRight, Bookmark, Smartphone, Sparkles, Star, Users2,
} from 'lucide-react';

const CURRENT_VERSION = 'v1.1';
const STORAGE_KEY = 'fp_whats_new_seen_version';

// Each slide: an icon, headline, body copy, and optional bullet list.
const SLIDES = [
  {
    icon: Bookmark,
    accent: '#ff6600',
    eyebrow: 'Saved Views',
    title: 'Bookmark any filter combination',
    body: 'Filter your Work Orders, Assets, Inspections, Vendors, Budget or PPM list — then save the URL as a named view. Apply it again with one click, share with your team, or pin to the Dashboard.',
    bullets: [
      'Search saved views by name, tag or folder',
      'Drag-and-drop to reorder; colour-code by folder',
      'Pin permalink slugs (e.g. `/v/critical-pumps`) into runbooks',
    ],
  },
  {
    icon: Star,
    accent: '#f59e0b',
    eyebrow: 'New in this release',
    title: 'Favourites, team defaults & analytics',
    body: 'Star any view to keep it within reach. Set a shared view as the team default for a route — every member sees the same starting filter automatically. Click any sparkline for a full 7/30/90-day analytics chart.',
    bullets: [
      'Per-view favourite star (star button on each row)',
      'Team defaults for shared views (admin / facility manager)',
      'Subscription email digests (daily / weekly)',
    ],
  },
  {
    icon: Smartphone,
    accent: '#0891b2',
    eyebrow: 'Mobile + iPad',
    title: 'A first-class hand-held experience',
    body: 'List pages now adapt to phone and tablet viewports — secondary columns collapse out, key meta stacks under each title. Touch targets enlarge automatically. Inputs no longer zoom on iOS.',
    bullets: [
      'Bottom-sheet menus on phones (Saved Views, filters)',
      'Safe-area aware layout for iPhone notches',
      'Fully wrapped Capacitor build for the App Store',
    ],
  },
  {
    icon: Users2,
    accent: '#111565',
    eyebrow: "What's next",
    title: "On the v1.2 horizon",
    body: 'WhatsApp + Microsoft Teams digests, vendor SLA scorecards, photo classification, calendar sync and more. The full roadmap is published in your repo (ROADMAP.md).',
  },
];

const WhatsNewModal = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (seen !== CURRENT_VERSION) {
        // Brief delay so the modal doesn't flash before the page paints.
        const t = setTimeout(() => setOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch {
      // localStorage may be blocked (private mode); fail open.
    }
  }, []);

  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;
  const Icon = slide.icon;
  const total = SLIDES.length;

  const dots = useMemo(
    () => Array.from({ length: total }, (_, i) => i),
    [total]
  );

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, CURRENT_VERSION);
    } catch { /* ignore */ }
    setOpen(false);
    setStep(0);
  };

  const next = () => {
    if (isLast) {
      dismiss();
      return;
    }
    setStep((s) => Math.min(s + 1, total - 1));
  };

  const prev = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="whats-new-modal" aria-describedby="whats-new-description">
        <VisuallyHidden>
          <DialogTitle>What&apos;s new in {CURRENT_VERSION}</DialogTitle>
          <DialogDescription id="whats-new-description">
            Brief tour of the latest improvements to FacilityPro.
          </DialogDescription>
        </VisuallyHidden>
        {/* Coloured banner */}
        <div
          className="relative px-6 py-8"
          style={{ background: `linear-gradient(135deg, ${slide.accent}, ${slide.accent}cc)` }}
        >
          <div className="flex items-center gap-2 text-white/85 text-xs uppercase tracking-wider mb-3">
            <Sparkles className="h-3.5 w-3.5" />
            {slide.eyebrow} · {CURRENT_VERSION}
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-white/15 flex items-center justify-center backdrop-blur-sm">
              <Icon className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-xl md:text-2xl font-semibold text-white leading-tight">
              {slide.title}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-foreground/80 leading-relaxed">{slide.body}</p>
          {slide.bullets && (
            <ul className="space-y-1.5">
              {slide.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0"
                    style={{ background: slide.accent }}
                  />
                  <span className="text-foreground/80">{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t bg-muted/30 flex-row items-center justify-between">
          <div className="flex items-center gap-1.5">
            {dots.map((i) => (
              <button
                type="button"
                key={i}
                onClick={() => setStep(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-[#111565]' : 'w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`whats-new-dot-${i}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={prev}
                data-testid="whats-new-prev"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              data-testid="whats-new-skip"
            >
              {isLast ? 'Done' : 'Skip'}
            </Button>
            {!isLast && (
              <Button
                size="sm"
                onClick={next}
                className="btn-primary"
                data-testid="whats-new-next"
              >
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {isLast && (
              <Button
                size="sm"
                onClick={dismiss}
                className="btn-primary"
                data-testid="whats-new-finish"
              >
                Got it
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewModal;
